import type { ClinicalDocument } from '@/features/encounters/api';
import type { PatientIdentity } from '@/features/patients/types';
import { CLINIC_TZ } from '@/lib/clinic-time';

/** Who and where a time slip belongs to: not in the document, so the page supplies it. */
export interface TimeSlipContext {
  patientName: string;
  identity: PatientIdentity | null;
  branchName: string;
}

export interface TimeSlipData {
  forWhom: 'patient' | 'companion';
  companionName: string | null;
  /** Masked: last 4 characters only. */
  companionId: string | null;
  /** YYYY-MM-DD at the branch, or null for a slip issued without one. */
  date: string | null;
  arrived: string;
  leaving: string;
}

/** "…4821": the last 4 letters or digits of an IC or passport, never the whole number. */
export function maskedId(value: string | null | undefined): string | null {
  const kept = (value ?? '').replace(/[^A-Za-z0-9]/g, '');
  return kept.length >= 4 ? `…${kept.slice(-4)}` : null;
}

const str = (v: unknown) =>
  typeof v === 'string' && v.trim() ? v.trim() : null;

/**
 * The slip's details, or null when the API sends none (roles without clinical access get
 * `data: {}`) or the times are missing: there is then nothing to print.
 */
export function timeSlipData(doc: ClinicalDocument): TimeSlipData | null {
  if (doc.kind !== 'time_slip') return null;
  const arrived = str(doc.data.arrived);
  const leaving = str(doc.data.leaving);
  if (!arrived || !leaving) return null;
  const companion = doc.data.for === 'companion';
  return {
    forWhom: companion ? 'companion' : 'patient',
    companionName: companion ? str(doc.data.companionName) : null,
    companionId: companion ? maskedId(str(doc.data.companionIc)) : null,
    date: str(doc.data.date),
    arrived,
    leaving,
  };
}

/** A slip can be printed while it is valid and its details are visible to this role. */
export function canPrintTimeSlip(doc: ClinicalDocument): boolean {
  return !doc.revokedAt && timeSlipData(doc) !== null;
}

/** "3 November 2026": the slip's own date, else the day it was issued at the clinic. */
export function slipDateLabel(data: TimeSlipData, issuedAt: string): string {
  const day = data.date?.match(/^\d{4}-\d{2}-\d{2}$/)
    ? new Date(`${data.date}T00:00:00Z`)
    : new Date(issuedAt);
  return day.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: data.date ? 'UTC' : CLINIC_TZ,
  });
}
